import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { API_BASE_URL } from "../../config/apiBase";
import { getAuthUserHeader } from "../../utils/userRoles";

const API = API_BASE_URL;

function toInt(v, fallback) {
  const n = parseInt(String(v ?? ""), 10);
  return Number.isNaN(n) ? fallback : n;
}

function normalizeConfig(raw) {
  const c = raw && typeof raw === "object" ? raw : {};
  return {
    ENABLE_DEMO_ONBOARDING:
      c.ENABLE_DEMO_ONBOARDING ?? c.enable_demo_onboarding ?? true,
    SHOW_DEMO_BEFORE_WEEK1_OBJECTIVES:
      c.SHOW_DEMO_BEFORE_WEEK1_OBJECTIVES ??
      c.show_demo_before_week1_objectives ??
      true,
  };
}

export default function WeekOneEntryGate({ children }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const rawStudentId = params.studentId || params.id || "SM01K4R9";
  const studentId = String(rawStudentId).toUpperCase();
  const inPortalLayout = Boolean(params.id) && !params.studentId;
  const routeWeek = toInt(params.weekId, 1);
  const routeLesson = toInt(params.lessonId, 1);

  const [config, setConfig] = useState({
    ENABLE_DEMO_ONBOARDING: true,
    SHOW_DEMO_BEFORE_WEEK1_OBJECTIVES: true,
  });
  const [configLoaded, setConfigLoaded] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(routeWeek);
  const [currentLesson, setCurrentLesson] = useState(routeLesson);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);
  const [guidedDemoCompletedOnce, setGuidedDemoCompletedOnce] = useState(false);
  const [redirectingToDemo, setRedirectingToDemo] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadConfig() {
      try {
        const res = await fetch(`${API}/api/config`, {
          cache: "no-store",
          headers: {
            "X-User": getAuthUserHeader(),
          },
        });
        const json = await res.json().catch(() => null);
        if (cancelled) return;
        setConfig(normalizeConfig(json));
      } catch {
        if (cancelled) return;
        setConfig(normalizeConfig(null));
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    }
    loadConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadDashboard() {
      try {
        const res = await fetch(
          `${API}/api/dashboard?student_id=${encodeURIComponent(studentId)}`,
          { cache: "no-store" }
        );
        const json = await res.json().catch(() => null);
        if (cancelled) return;
        setCurrentWeek(toInt(json?.week, routeWeek));
        setCurrentLesson(toInt(json?.lesson, routeLesson));
        setGuidedDemoCompletedOnce(Boolean(json?.guided_demo_completed_once));
      } catch {
        if (cancelled) return;
        setCurrentWeek(routeWeek);
        setCurrentLesson(routeLesson);
        setGuidedDemoCompletedOnce(false);
      } finally {
        if (!cancelled) setDashboardLoaded(true);
      }
    }

    if (!studentId) {
      setDashboardLoaded(true);
      return;
    }
    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [studentId, routeWeek, routeLesson]);

  const onWeek1EntryPath = routeWeek === 1 && routeLesson === 1;
  const returningFromDemoCompletion = useMemo(() => {
    const qp = new URLSearchParams(location.search);
    return qp.get("from_demo_completion") === "1";
  }, [location.search]);
  const canEvaluate = configLoaded && dashboardLoaded;

  useEffect(() => {
    if (!onWeek1EntryPath || !canEvaluate) return;
    const shouldAutoLaunch =
      Number(currentWeek) === 1 &&
      Number(currentLesson) === 1 &&
      config?.ENABLE_DEMO_ONBOARDING === true &&
      config?.SHOW_DEMO_BEFORE_WEEK1_OBJECTIVES === true &&
      !guidedDemoCompletedOnce &&
      !returningFromDemoCompletion;

    if (!shouldAutoLaunch) return;

    const canonicalReturnTo = `${location.pathname}${location.search || ""}`;
    const query = new URLSearchParams({
      source: "auto_w1l1",
      studentId,
      returnTo: canonicalReturnTo,
    });
    setRedirectingToDemo(true);
    const demoPath = inPortalLayout
      ? `/student/dashboard/${studentId}/guided-demo?${query.toString()}`
      : `/guided-demo?${query.toString()}`;
    navigate(demoPath, { replace: true });
  }, [
    canEvaluate,
    config,
    currentLesson,
    currentWeek,
    guidedDemoCompletedOnce,
    location.pathname,
    location.search,
    navigate,
    onWeek1EntryPath,
    returningFromDemoCompletion,
    studentId,
    inPortalLayout,
  ]);

  if (!onWeek1EntryPath) {
    return children;
  }

  if (!canEvaluate) {
    return (
      <div className="lesson-wrap">
        <div style={{ height: "48px" }} />
        <div className="lp-loading">Loading...</div>
      </div>
    );
  }

  if (redirectingToDemo) {
    return (
      <div className="lesson-wrap">
        <div style={{ height: "48px" }} />
        <div className="lp-loading">Opening guided demo...</div>
      </div>
    );
  }

  return children;
}
